import React, { useState, useEffect } from "react";
import {
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Divider,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssessmentIcon from "@mui/icons-material/Assessment";
import StarIcon from "@mui/icons-material/Star";
import { honorariosDashboardJWT } from "../../api/endpoints";

export default function HonorariosDashboard() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        evolucion_mensual: [],
        distribucion_estado: [],
        top_certificados: [],
        totales: {
            total_cobrado: 0,
            total_en_proceso: 0,
            total_pendiente: 0,
            total_solicitudes: 0,
            cant_cobrado: 0,
            cant_en_proceso: 0,
            cant_pendiente: 0,
        },
    });

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const response = await honorariosDashboardJWT();
            if (response.ok) {
                setData({
                    evolucion_mensual: response.evolucion_mensual || [],
                    distribucion_estado: response.distribucion_estado || [],
                    top_certificados: response.top_certificados || [],
                    totales: response.totales || {
                        total_cobrado: 0,
                        total_en_proceso: 0,
                        total_pendiente: 0,
                        total_solicitudes: 0,
                    },
                });
            }
        } catch (error) {
            if (error?.code === "SESSION_EXPIRED") {
                throw error;
            }
            console.error("Error fetching dashboard data", error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return `$ ${parseFloat(value).toLocaleString("es-AR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const getMaxValue = (arr, key) => {
        if (arr.length === 0) return 1;
        return Math.max(...arr.map((item) => parseFloat(item[key] || 0)));
    };

    const ESTADO_COLORS = {
        0: "#FFA726", // Pendiente - Naranja
        1: "#66BB6A", // Pagada - Verde
        2: "#42A5F5", // En Curso - Azul
        4: "#AB47BC", // Cerrada - Púrpura
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Totales Generales */}
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card sx={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        borderRadius: "4px",
                        height: "100%",
                    }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" style={{ opacity: 0.9, marginBottom: 4, display: 'block' }}>
                                Total Cobrado
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: 700 }}>
                                {formatCurrency(data.totales.total_cobrado)}
                            </Typography>
                            <Typography variant="caption" style={{ opacity: 0.8 }}>
                                {data.totales.cant_cobrado} solicitudes pagadas
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card sx={{
                        background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        color: "white",
                        borderRadius: "4px",
                        height: "100%",
                    }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" style={{ opacity: 0.9, marginBottom: 4, display: 'block' }}>
                                En Proceso
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: 700 }}>
                                {formatCurrency(data.totales.total_en_proceso)}
                            </Typography>
                            <Typography variant="caption" style={{ opacity: 0.8 }}>
                                {data.totales.cant_en_proceso} solicitudes sin cobrar
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card sx={{
                        background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        color: "white",
                        borderRadius: "4px",
                        height: "100%",
                    }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            {/* Esta card NO mide solicitudes: mide los certificados que
                                el abogado todavía no metió en ninguna solicitud. Es el
                                histórico completo, así que no tiene por qué coincidir con
                                la pestaña "Certificados Pendientes", que filtra por fecha. */}
                            <Typography variant="caption" style={{ opacity: 0.9, marginBottom: 4, display: 'block' }}>
                                Pendiente de Solicitar
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: 700 }}>
                                {formatCurrency(data.totales.total_pendiente)}
                            </Typography>
                            <Typography variant="caption" style={{ opacity: 0.8 }}>
                                {data.totales.cant_pendiente} certificados sin solicitar
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card sx={{
                        background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                        color: "white",
                        borderRadius: "4px",
                        height: "100%",
                    }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" style={{ opacity: 0.9, marginBottom: 4, display: 'block' }}>
                                Total Solicitudes
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: 700 }}>
                                {data.totales.total_solicitudes}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card sx={{
                        background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                        color: "white",
                        borderRadius: "4px",
                        height: "100%",
                    }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" style={{ opacity: 0.9, marginBottom: 4, display: 'block' }}>
                                Promedio por Solicitud
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: 700 }}>
                                {formatCurrency(data.totales.cant_cobrado > 0 ? data.totales.total_cobrado / data.totales.cant_cobrado : 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                {/* Evolución Mensual */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ p: 3, borderRadius: "4px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <TrendingUpIcon />
                            Evolución Mensual (Últimos 12 meses)
                        </Typography>
                        <Divider style={{ marginBottom: 16 }} />
                        {data.evolucion_mensual.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center">
                                No hay datos disponibles
                            </Typography>
                        ) : (
                            data.evolucion_mensual.map((item) => {
                                const maxVal = getMaxValue(data.evolucion_mensual, "cobrado");
                                const cobradoPercent = (parseFloat(item.cobrado) / maxVal) * 100;
                                const enProcesoPercent = (parseFloat(item.en_proceso) / maxVal) * 100;

                                return (
                                    <Box key={item.mes} sx={{ mb: 2 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: "4px", color: "text.secondary" }}>{item.mes}</Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Box sx={{
                                                flex: 1,
                                                height: 24,
                                                backgroundColor: "#e0e0e0",
                                                borderRadius: "4px",
                                                overflow: "hidden",
                                                position: "relative",
                                            }}>
                                                <Box
                                                    sx={{
                                                        height: "100%",
                                                        borderRadius: "4px",
                                                        transition: "width 0.5s ease",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "flex-end",
                                                        pr: "8px",
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        color: "white",
                                                    }}
                                                    style={{
                                                        width: `${cobradoPercent}%`,
                                                        background: "linear-gradient(90deg, #66BB6A 0%, #43A047 100%)",
                                                    }}
                                                >
                                                    {item.cant_cobrado > 0 && `${item.cant_cobrado}`}
                                                </Box>
                                            </Box>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 80, textAlign: "right" }}>
                                                {formatCurrency(item.cobrado)}
                                            </Typography>
                                        </Box>
                                        {parseFloat(item.en_proceso) > 0 && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "4px" }}>
                                                <Box sx={{
                                                    flex: 1,
                                                    height: 24,
                                                    backgroundColor: "#e0e0e0",
                                                    borderRadius: "4px",
                                                    overflow: "hidden",
                                                    position: "relative",
                                                }}>
                                                    <Box
                                                        sx={{
                                                            height: "100%",
                                                            borderRadius: "4px",
                                                            transition: "width 0.5s ease",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "flex-end",
                                                            pr: "8px",
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            color: "white",
                                                        }}
                                                        style={{
                                                            width: `${enProcesoPercent}%`,
                                                            background: "linear-gradient(90deg, #42A5F5 0%, #1E88E5 100%)",
                                                        }}
                                                    >
                                                        {item.cant_en_proceso > 0 && `${item.cant_en_proceso}`}
                                                    </Box>
                                                </Box>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 80, textAlign: "right" }}>
                                                    {formatCurrency(item.en_proceso)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })
                        )}
                        <Box mt={2}>
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Box width={16} height={16} bgcolor="#66BB6A" borderRadius="1px" />
                                        <Typography variant="caption">Cobrado</Typography>
                                    </Box>
                                </Grid>
                                <Grid size={6}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Box width={16} height={16} bgcolor="#42A5F5" borderRadius="1px" />
                                        <Typography variant="caption">En Proceso</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>

                {/* Distribución por Estado */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 3, borderRadius: "4px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <AssessmentIcon />
                            Por Estado
                        </Typography>
                        <Divider style={{ marginBottom: 16 }} />
                        {data.distribucion_estado.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center">
                                No hay datos
                            </Typography>
                        ) : (
                            <>
                                {data.distribucion_estado.map((item) => {
                                    const total = data.distribucion_estado.reduce(
                                        (sum, i) => sum + parseFloat(i.total),
                                        0
                                    );
                                    const percentage = total > 0 ? ((parseFloat(item.total) / total) * 100).toFixed(1) : 0;

                                    return (
                                        <Box key={item.id_estado} mb={2}>
                                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                    {item.estado}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {percentage}%
                                                </Typography>
                                            </Box>
                                            <Box
                                                height={8}
                                                bgcolor="#e0e0e0"
                                                borderRadius="4px"
                                                overflow="hidden"
                                            >
                                                <Box
                                                    height="100%"
                                                    bgcolor={ESTADO_COLORS[item.id_estado] || "#757575"}
                                                    width={`${percentage}%`}
                                                    style={{ transition: "width 0.5s ease" }}
                                                />
                                            </Box>
                                            <Box display="flex" justifyContent="space-between" mt={0.5}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.cantidad} {item.cantidad === 1 ? "solicitud" : "solicitudes"}
                                                </Typography>
                                                <Typography variant="caption" style={{ fontWeight: 600 }}>
                                                    {formatCurrency(item.total)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </>
                        )}
                    </Paper>
                </Grid>

                {/* Top Certificados */}
                <Grid size={12}>
                    <Paper sx={{ p: 3, borderRadius: "4px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <StarIcon />
                            Top 5 Certificados más Altos
                        </Typography>
                        <Divider style={{ marginBottom: 16 }} />
                        {data.top_certificados.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center">
                                No hay datos disponibles
                            </Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Certificado</TableCell>
                                            <TableCell>Autos</TableCell>
                                            <TableCell>Demandado</TableCell>
                                            <TableCell>Fecha</TableCell>
                                            <TableCell align="right">Monto</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.top_certificados.map((cert, index) => (
                                            <TableRow key={index} hover sx={{ backgroundColor: index % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell style={{ fontWeight: 600 }}>
                                                    {cert.certificado}
                                                </TableCell>
                                                <TableCell>{cert.autos}</TableCell>
                                                <TableCell>{cert.demandado}</TableCell>
                                                <TableCell>{cert.fecha}</TableCell>
                                                <TableCell align="right" style={{ fontWeight: 600, color: "#43A047" }}>
                                                    {formatCurrency(cert.monto)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
